// EvolutionSequence — the star-up ceremony. Two forged-card
// miniatures arc into the center and SLAM (white flash + ink-outlined
// comic starburst + paper confetti — the battle stage's impact
// vocabulary), the result re-folds itself through the unfold-stage
// assets, then the critter pops in under a falling EVOLVED stamp
// with the stat pellets counting up beneath.
//
// Shop mounts this fullscreen (over .shop-evo-overlay) for STAR-UPS
// only — plain fill ticks play a short in-place effect at the board
// slot instead (see BoardSlot's fillFx). The design gallery also
// embeds this inline; non-starUp props render the same flow minus the
// stamp.
//
// All sizing is clamp()/vmin-driven so the one composition reads on
// desktop, phone-landscape, and the Fold without per-breakpoint
// forks. Timing is percentage-based against a single --evo-d var.

import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react'
import type { Element, GlyphKey } from '@origami/shared'
import { TieredCritter, ELEMENT_INFO, HeartStatImg, StrengthStatImg } from '../design-system'
import { playSfx } from '../sound'
import './EvolutionSequence.css'

export interface EvolutionUnit {
  glyph: GlyphKey
  name: string
  element: Element
  hp: number
  atk: number
  star: 1 | 2 | 3
  paper?: string
}

export interface EvolutionSequenceProps {
  sourceA: EvolutionUnit
  sourceB: EvolutionUnit
  result: EvolutionUnit
  tickedDot: 1 | 2 | 3
  starUp?: boolean
  onComplete?: () => void
  durationMs?: number
}

const STAR_PAPER: Record<Element, [string, string, string]> = {
  fire:  ['#ffe2d6', '#ffc7a8', '#ff9b6e'],
  water: ['#d9eaf2', '#a9c8de', '#6c9fc4'],
  wind:  ['#fff1c2', '#ffe5a0', '#ffd76e'],
  wood:  ['#e0ecca', '#c5dba0', '#9bc070'],
  earth: ['#efe6d4', '#d9c8a4', '#bda66e'],
}

function paperFor(unit: EvolutionUnit): string {
  return unit.paper ?? STAR_PAPER[unit.element][unit.star - 1]!
}

const UNFOLD_STAGES = [
  '/glyph-assets/unfold-flat.png',
  '/glyph-assets/unfold-fold1.png',
  '/glyph-assets/unfold-fold2.png',
  '/glyph-assets/unfold-fold3.png',
]

/** Paper confetti scattered by the slam — randomized per mount. */
interface ConfettiPiece {
  dx: number
  dy: number
  rot: number
  size: number
  tinted: boolean
  delayPct: number
}

/** Extra time the completed ceremony lingers on screen (final frame
 *  held via the CSS `forwards` fill) before auto-dismissing. The
 *  player can always click/tap the scrim to skip it sooner. */
export const EVO_HOLD_MS = 1700

export function EvolutionSequence({
  sourceA,
  sourceB,
  result,
  tickedDot,
  starUp = false,
  onComplete,
  durationMs = 3200,
}: EvolutionSequenceProps): ReactElement {
  useEffect(() => {
    if (!onComplete) return
    // Animations finish at durationMs and hold their final frame; the
    // auto-dismiss waits an extra EVO_HOLD_MS so the reveal lingers.
    const t = setTimeout(onComplete, durationMs + EVO_HOLD_MS)
    return () => clearTimeout(t)
  }, [onComplete, durationMs])

  // Audio beats synced to the CSS timeline (fractions of durationMs):
  // slam at 26%, the unfold riffle as the folds run (30%), the
  // sparkle as the critter pops (66%), the hanko stamp at 74%.
  useEffect(() => {
    const at = (frac: number, fn: () => void) =>
      window.setTimeout(fn, durationMs * frac)
    const timers = [
      at(0.25, () => playSfx('combine-slam', { ignoreThrottle: true })),
      at(0.3, () => playSfx('combine-fold', { ignoreThrottle: true })),
      at(0.66, () => playSfx('combine-sparkle', { ignoreThrottle: true })),
    ]
    if (starUp) {
      timers.push(at(0.74, () => playSfx('combine-stamp', { ignoreThrottle: true })))
    }
    return () => timers.forEach(window.clearTimeout)
  }, [durationMs, starUp])

  // Stat pellets count up from the stronger parent's line to the
  // result's during the reveal window (74% → 90% of the run).
  const fromHp = Math.max(sourceA.hp, sourceB.hp)
  const fromAtk = Math.max(sourceA.atk, sourceB.atk)
  const [shown, setShown] = useState({ hp: fromHp, atk: fromAtk })
  useEffect(() => {
    setShown({ hp: fromHp, atk: fromAtk })
    const startAt = durationMs * 0.74
    const span = durationMs * 0.16
    let raf = 0
    const begin = performance.now() + startAt
    const tick = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - begin) / span))
      setShown({
        hp: Math.round(fromHp + (result.hp - fromHp) * t),
        atk: Math.round(fromAtk + (result.atk - fromAtk) * t),
      })
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [durationMs, fromHp, fromAtk, result.hp, result.atk])

  const confetti = useMemo<ConfettiPiece[]>(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2 + Math.random() * 0.5
        const r = 70 + Math.random() * 90
        return {
          dx: Math.cos(a) * r,
          dy: Math.sin(a) * r - 30,
          rot: (Math.random() * 2 - 1) * 540,
          size: 7 + Math.random() * 9,
          tinted: i % 3 === 0,
          delayPct: Math.random() * 3,
        }
      }),
    [],
  )

  const info = ELEMENT_INFO[result.element]
  const vars = {
    '--evo-d': `${durationMs}ms`,
    '--evo-accent': info.bg,
    '--evo-deep': `var(--el-${result.element}-deep)`,
  } as CSSProperties

  return (
    <div className={`evo2${starUp ? ' evo2--starup' : ''}`} style={vars}>
      {/* faint element watermark behind everything */}
      <span className="evo2__kanji" aria-hidden>
        {info.kanji}
      </span>

      {/* the two source cards arcing in */}
      <div className="evo2__card evo2__card--l">
        <CeremonyCard unit={sourceA} />
      </div>
      <div className="evo2__card evo2__card--r">
        <CeremonyCard unit={sourceB} />
      </div>

      {/* slam: flash + ink-outlined comic star + paper confetti */}
      <div className="evo2__flash" aria-hidden />
      <svg
        className="evo2__burst"
        viewBox="0 0 120 120"
        aria-hidden
      >
        <path d={starPath(12)} fill="var(--evo-accent)" stroke="var(--ink)" strokeWidth="4" strokeLinejoin="round" />
        <path d={starPath(34)} fill="#fff" opacity="0.9" />
      </svg>
      {confetti.map((p, i) => (
        <span
          key={i}
          className="evo2__confetti"
          aria-hidden
          style={
            {
              '--cx': `${p.dx}px`,
              '--cy': `${p.dy}px`,
              '--crot': `${p.rot}deg`,
              width: p.size,
              height: p.size * 1.4,
              background: p.tinted ? 'var(--evo-accent)' : 'var(--washi-light)',
              animationDelay: `calc(var(--evo-d) * 0.0${p.delayPct.toFixed(0)})`,
            } as CSSProperties
          }
        />
      ))}

      {/* the re-fold: flat sheet → folds → the evolved critter */}
      <div className="evo2__center">
        <div className="evo2__halo" aria-hidden />
        {UNFOLD_STAGES.map((src, i) => (
          <img key={src} src={src} alt="" className={`evo2__fold evo2__fold--${i + 1}`} />
        ))}
        <div className="evo2__critter">
          <TieredCritter glyph={result.glyph} star={result.star} size={150} />
        </div>
      </div>

      {/* stamp — star-ups only */}
      {starUp && (
        <div className="evo2__stamp" aria-hidden>
          <span className="evo2__stamp-word">EVOLVED</span>
          <span className="evo2__stamp-stars">{'★'.repeat(result.star)}</span>
        </div>
      )}

      {/* drifting petals over the reveal */}
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className="evo2__petal"
          aria-hidden
          style={{
            left: `${22 + i * 19}%`,
            animationDelay: `calc(var(--evo-d) * ${(0.68 + i * 0.05).toFixed(2)})`,
          }}
        />
      ))}

      {/* name + counting stat pellets + fold-progress hankos */}
      <div className="evo2__tag">
        <div className="evo2__tag-name">
          {result.name}
          <span className="evo2__tag-stars">{'✦'.repeat(result.star)}</span>
        </div>
        <div className="evo2__tag-stats">
          <HeartStatImg value={shown.hp} size={38} />
          <StrengthStatImg value={shown.atk} size={38} />
        </div>
        <div className="evo2__tag-dots">
          {([1, 2, 3] as const).map((n) => (
            <span
              key={n}
              className={`evo2__dot${
                n < tickedDot ? ' evo2__dot--filled' : n === tickedDot ? ' evo2__dot--ticking' : ''
              }`}
            >
              {n}
            </span>
          ))}
        </div>
      </div>

      <span className="evo2__hint">tap to skip ▸</span>
    </div>
  )
}

/** 8-point comic star path centered in a 120×120 viewBox. `inner`
 *  sets the valley radius (smaller = spikier). */
function starPath(inner: number): string {
  const pts: string[] = []
  for (let i = 0; i < 16; i++) {
    const r = i % 2 === 0 ? 56 : inner
    const a = (i / 16) * Math.PI * 2 - Math.PI / 2
    pts.push(`${60 + Math.cos(a) * r},${60 + Math.sin(a) * r}`)
  }
  return `M ${pts.join(' L ')} Z`
}

/** Miniature of the forged shop card — washi face, element ring,
 *  star seal, critter — so the ceremony visibly merges the cards the
 *  player just dragged together. */
function CeremonyCard({ unit }: { unit: EvolutionUnit }): ReactElement {
  return (
    <div
      className="evo2__ccard"
      style={{
        background: paperFor(unit),
        boxShadow: `0 4px 0 var(--ink), inset 0 0 0 3px ${ELEMENT_INFO[unit.element].bg}`,
      }}
    >
      <div className="evo2__ccard-seal">{'✦'.repeat(unit.star)}</div>
      <TieredCritter glyph={unit.glyph} star={unit.star} size={64} />
    </div>
  )
}
